import { getQueryParam, setQueryParam } from './url';

const replacePlusSign = (s: string): string => s.replace(/\+/g, '%20');

/**
 * Creates a formatted string to use with srcset in responsive images
 * @param {array} images array of objects as { src: {string} this images source,
 * width: {number} how wide this image is }
 * @return {string} Formatted string, ex: meme.jpg?w=1920 1920w, ...
 */
const formatSrcSet = (images: IImageSize[]): string => {
  // Prepare an empty array
  const imageArray: string[] = [];

  // Map the proviced source and it's width to our array
  images.map(({ src, width }) => imageArray.push(`${src} ${width}w`));

  // Returns the array with comma separator
  return imageArray.join(', ');
};

/**
 * Creates a formatted string to use with sizes attribute in responsive images.
 * @param {array} sizes array of objects as { breakpoint: {number} widest screen
 * size for this image width, width: {number} the image size that should be use
 * for this breakpoint }
 * @return {string} Formatted string, ex: (max-width: 375px) 375px, ...
 */
const formatImageSizes = (sizes: IImageSize[]): string => {
  // Prepare an empty array
  const sizeArray: string[] = [];

  // Map the provided breakpoints to its matching size
  sizes.map(({ breakpoint, width }) => sizeArray.push(`(max-width: ${breakpoint}px) ${width}px`));

  // Returns the array with comma separator
  return sizeArray.join(', ');
};

interface IFormatImage {
  width?: number;
  height?: number;
  crop?: boolean;
  cdn?: boolean;
  output?: string;
  quality?: number;
  usm?: number;
}

export const formatImage = (
  url: string,
  {
    width,
    height,
    crop,
    cdn,
    output,
    quality, // Only if cdn is false
    usm,
  }: IFormatImage,
): string => {
  if (!url) {
    return url;
  }

  try {
    let _url = url;
    const WParam = cdn ? 'width' : 'w';
    const hParam = cdn ? 'height' : 'h';
    const cropParam = cdn ? 'crop' : 'fit';
    const outputParam = cdn ? 'output' : 'fm';
    const fit = getQueryParam(cropParam, url);
    const startWidth = getQueryParam(WParam, url);
    const startHeight = getQueryParam(hParam, url);
    const croppedDefault = fit && fit.includes(cdn ? 'true' : 'crop');

    if (
      croppedDefault &&
      startWidth &&
      startHeight &&
      width !== undefined &&
      !isNaN(width) &&
      !height
    ) {
      // if original image url is cropped, keep ratio.
      const widthAsNumber = parseInt(startWidth, 10);
      const heightAsNumber = parseInt(startHeight, 10);
      const ratio = heightAsNumber / widthAsNumber;
      _url = setQueryParam(WParam, `${width}`, _url);
      _url = setQueryParam(hParam, `${ratio * width}`, _url);
    } else {
      if (width !== undefined && !isNaN(width)) {
        _url = setQueryParam(WParam, `${width}`, _url);
      }
      if (height !== undefined && !isNaN(height)) {
        _url = setQueryParam(hParam, `${height}`, _url);
      }
      if (crop) {
        _url = setQueryParam(cropParam, cdn ? 'true' : 'crop', _url);
      } else if (crop === false && cdn) {
        _url = setQueryParam(cropParam, 'false', _url);
      }
    }

    if (output) {
      _url = setQueryParam(outputParam, output, _url);
    }

    if (quality && !cdn) {
      _url = setQueryParam('auto', '', _url);
      _url = setQueryParam('q', quality, _url);
    }

    if (usm) {
      _url = setQueryParam('usm', usm, _url);
    }

    return replacePlusSign(_url);
  } catch (_) {
    return replacePlusSign(url);
  }
};

/**
 * Takes an image source, one or more image sizes, and returns an object prepared for
 * responsive images, set cdn to true to prepare strings for cdn instead of static.
 * @param {string} src
 * @param {array} imageSizes
 * @param {boolean} cdn wheather to use cdn or static properties
 * @param {string} output specify image format
 * @return {object} { src: string, srcSet: string, sizes: string }
 */
export const mapImageSources = (
  src: string,
  imageSizes: IImageSize[],
  cdn?: boolean,
  output?: string,
): IResponsiveImage => {
  // Take original image source and format it for each image size
  imageSizes.map((imageSize) => {
    imageSize.src = formatImage(src, { width: imageSize.width, cdn, output });
  });

  // Get formatted srcset string
  const srcSet = formatSrcSet(imageSizes);

  // Get formated sizes string
  const sizes = formatImageSizes(imageSizes);

  // Build object and return it
  return {
    src,
    srcSet,
    sizes,
  };
};
